import { useGetList } from 'react-admin';
import { Box, Button, Card, CardContent, Stack, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { useNavigate } from 'react-router-dom';
import type { ReactNode } from 'react';

interface Dep {
  resource: string;
  label: string;
  path: string;
  reason: string;
}

const DependencyBlocker = ({ all, unmet }: { all: Dep[]; unmet: Dep[] }) => {
  const navigate = useNavigate();
  return (
    <Box display="flex" justifyContent="center" pt={8} px={3}>
      <Card sx={{ maxWidth: 560, width: '100%' }}>
        <CardContent sx={{ p: 4 }}>
          <Box display="flex" alignItems="center" gap={1.5} mb={1.5}>
            <LockOutlinedIcon sx={{ color: 'warning.main', fontSize: 26 }} />
            <Typography variant="h6" fontWeight={700}>
              Complete these steps first
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" mb={3}>
            This section requires the following before you can proceed:
          </Typography>
          <Stack spacing={1.5}>
            {all.map((dep) => {
              const met = !unmet.some((u) => u.resource === dep.resource);
              return (
                <Box
                  key={dep.resource}
                  sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 2,
                    p: 1.5,
                    borderRadius: 1,
                    border: '1px solid',
                    bgcolor: (theme) =>
                      alpha(met ? theme.palette.success.main : theme.palette.warning.main, 0.08),
                    borderColor: met ? 'success.light' : 'warning.light',
                  }}
                >
                  <Box flex={1}>
                    <Typography
                      variant="body2"
                      fontWeight={600}
                      sx={{
                        color: met ? 'text.disabled' : 'text.primary',
                        textDecoration: met ? 'line-through' : 'none',
                      }}
                    >
                      {dep.label}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {dep.reason}
                    </Typography>
                  </Box>
                  {met ? (
                    <CheckCircleOutlineIcon sx={{ color: 'success.main', fontSize: 22, mt: 0.25, flexShrink: 0 }} />
                  ) : (
                    <Button
                      size="small"
                      variant="contained"
                      color="warning"
                      endIcon={<ArrowForwardIcon />}
                      onClick={() => navigate(dep.path)}
                      sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}
                    >
                      Go to {dep.label}
                    </Button>
                  )}
                </Box>
              );
            })}
          </Stack>
          <Typography
            variant="caption"
            color="text.disabled"
            sx={{ display: 'block', mt: 3, textAlign: 'center' }}
          >
            Workflow order: 1 Build → 2 Staff → 3 Assess → 4 Record → 5 Review
          </Typography>
        </CardContent>
      </Card>
    </Box>
  );
};

// ─── Dep definitions ─────────────────────────────────────────────────────────

const EVENTS_DEP: Dep = {
  resource: 'events',
  label: 'Assessment Events',
  path: '/events',
  reason: 'Events define the physical tests that scoring tables grade and assessments bundle.',
};

const ASSESSMENTS_DEP: Dep = {
  resource: 'assessments',
  label: 'Assessments',
  path: '/assessments',
  reason: 'Assessments bundle events into a formal test battery. Required before scheduling or recording results.',
};

const INSTANCES_DEP: Dep = {
  resource: 'instances',
  label: 'Assessment Schedule',
  path: '/instances',
  reason: 'A scheduled assessment instance identifies the explicit context for governed participation workflows.',
};

const COURSES_DEP: Dep = {
  resource: 'courses',
  label: 'Courses',
  path: '/courses',
  reason: 'Classes must be linked to a course. Create at least one course first.',
};

const STUDENTS_DEP: Dep = {
  resource: 'students',
  label: 'Students',
  path: '/students',
  reason: 'Students must exist before they can be grouped into teams or have results recorded.',
};

const STUDENTS_2_DEP: Dep = {
  resource: 'students',
  label: 'Students (at least 2)',
  path: '/students',
  reason: 'Teams require at least 2 student members.',
};

// ─── Named gates ─────────────────────────────────────────────────────────────

export const RequireEvents = ({ children }: { children: ReactNode }) => {
  const { data, isLoading } = useGetList('events', {
    pagination: { page: 1, perPage: 1 },
    sort: { field: 'id', order: 'ASC' },
  });
  if (isLoading) return null;
  if ((data?.length ?? 0) < 1) {
    return <DependencyBlocker all={[EVENTS_DEP]} unmet={[EVENTS_DEP]} />;
  }
  return <>{children}</>;
};

export const RequireAssessments = ({ children }: { children: ReactNode }) => {
  const { data, isLoading } = useGetList('assessments', {
    pagination: { page: 1, perPage: 1 },
    sort: { field: 'id', order: 'ASC' },
  });
  if (isLoading) return null;
  if ((data?.length ?? 0) < 1) {
    return <DependencyBlocker all={[ASSESSMENTS_DEP]} unmet={[ASSESSMENTS_DEP]} />;
  }
  return <>{children}</>;
};

export const RequireAssessmentsAndStudents = ({ children }: { children: ReactNode }) => {
  const { data: da, isLoading: la } = useGetList('assessments', {
    pagination: { page: 1, perPage: 1 },
    sort: { field: 'id', order: 'ASC' },
  });
  const { data: ds, isLoading: ls } = useGetList('students', {
    pagination: { page: 1, perPage: 1 },
    sort: { field: 'id', order: 'ASC' },
  });
  if (la || ls) return null;
  const unmet: Dep[] = [];
  if ((da?.length ?? 0) < 1) unmet.push(ASSESSMENTS_DEP);
  if ((ds?.length ?? 0) < 1) unmet.push(STUDENTS_DEP);
  if (unmet.length > 0) {
    return <DependencyBlocker all={[ASSESSMENTS_DEP, STUDENTS_DEP]} unmet={unmet} />;
  }
  return <>{children}</>;
};

export const RequireInstancesAndStudents = ({ children }: { children: ReactNode }) => {
  const { data: di, isLoading: li } = useGetList('instances', {
    pagination: { page: 1, perPage: 1 },
    sort: { field: 'id', order: 'ASC' },
  });
  const { data: ds, isLoading: ls } = useGetList('students', {
    pagination: { page: 1, perPage: 1 },
    sort: { field: 'id', order: 'ASC' },
  });
  if (li || ls) return null;
  const unmet: Dep[] = [];
  if ((di?.length ?? 0) < 1) unmet.push(INSTANCES_DEP);
  if ((ds?.length ?? 0) < 1) unmet.push(STUDENTS_DEP);
  if (unmet.length > 0) {
    return <DependencyBlocker all={[INSTANCES_DEP, STUDENTS_DEP]} unmet={unmet} />;
  }
  return <>{children}</>;
};

export const RequireCourses = ({ children }: { children: ReactNode }) => {
  const { data, isLoading } = useGetList('courses', {
    pagination: { page: 1, perPage: 1 },
    sort: { field: 'id', order: 'ASC' },
  });
  if (isLoading) return null;
  if ((data?.length ?? 0) < 1) {
    return <DependencyBlocker all={[COURSES_DEP]} unmet={[COURSES_DEP]} />;
  }
  return <>{children}</>;
};

export const RequireStudents = ({ children }: { children: ReactNode }) => {
  const { data, isLoading } = useGetList('students', {
    pagination: { page: 1, perPage: 2 },
    sort: { field: 'id', order: 'ASC' },
  });
  if (isLoading) return null;
  if ((data?.length ?? 0) < 2) {
    return <DependencyBlocker all={[STUDENTS_2_DEP]} unmet={[STUDENTS_2_DEP]} />;
  }
  return <>{children}</>;
};
